import React from 'react';
import {
  ArrowRight,
  ArrowLeft,
  LayoutGrid,
  Sparkles,
  ScanLine,
  Search,
  Bell,
  Store,
} from 'lucide-react';
import PublicHeader from './PublicHeader';
import './landing/luxe/luxe.css';

const FEATURES = [
  {
    icon: LayoutGrid,
    title: 'Catalogues in minutes, not meetings',
    desc: 'Spin up an interactive fabric catalogue your team and buyers can actually use—built fast, refreshed anytime.',
  },
  {
    icon: Sparkles,
    title: 'Concepts in seconds',
    desc: 'Visualise garment ideas on your real fabrics so “what if?” becomes “that’s it”—before you cut a sample.',
  },
  {
    icon: ScanLine,
    title: 'AI reads the cloth from one image',
    desc: 'Upload a photo and let our models infer rich fabric signals—no spreadsheets required to get started.',
  },
  {
    icon: Search,
    title: 'Search by meaning and by look',
    desc: 'Blend semantic queries with visual similarity so discovery feels natural, whether they type “soft drape linen” or point at a swatch.',
  },
  {
    icon: Bell,
    title: 'New collection notification to all followers',
    desc: 'When your catalogue goes live, your followers get notified instantly—so the next decision happens faster.',
  },
  {
    icon: Store,
    title: 'One click for push to market place',
    desc: 'Publish your catalogue to the marketplace in one step and let buyers discover it without extra manual sharing.',
  },
];

const STEPS = [
  'Add fabrics—our AI enriches what it sees in the image so each card feels complete.',
  'Let buyers explore with visual + semantic search; watch concepts render on those textiles in moments.',
  'Publish your interactive catalogue and share—so decisions land while interest is still hot.',
];

const CatalogueInfoPage = ({ onBack, onCreateCatalogue, isAuthenticated, setActiveView, onSupportClick }) => {
  return (
    <div className="luxe-root min-h-screen">
      <PublicHeader
        activeViewId="catalogue-info"
        setActiveView={setActiveView}
        variant="dark"
        onSupportClick={onSupportClick}
      />

      <section className="relative pt-24 sm:pt-28 pb-20 px-4 sm:px-6 luxe-bg-gradient luxe-grain min-h-screen">
        <div className="luxe-aurora" />
        <div className="relative container mx-auto max-w-6xl">
          <div className="flex items-center justify-between gap-3 mb-10">
            <button type="button" onClick={onBack} className="luxe-btn-ghost !px-4 !py-2 text-sm">
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl overflow-hidden bg-white flex items-center justify-center">
                <img src="/logo.png?v=20260711" alt="Fashdeck" className="w-full h-full object-contain" />
              </div>
              <div className="leading-tight">
                <p className="text-sm font-semibold text-white">Create free catalogue</p>
                <p className="text-xs text-white/45">Fabric-first, buyer-ready, link-in-hand</p>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-start">
            <div className="relative lg:sticky lg:top-24 order-1 lg:order-2">
              <div className="absolute -inset-6 bg-gradient-to-r from-gold/20 to-gold-deep/10 blur-3xl" />
              <div className="relative luxe-glass overflow-hidden">
                <div className="border-b border-white/10">
                  <img
                    src="/catalog.gif"
                    alt="Interactive fabric catalogue preview"
                    className="w-full h-auto object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="p-6 sm:p-8">
                  <p className="text-sm font-semibold text-white">From fabric pile to “send me the link”</p>
                  <ol className="mt-4 space-y-3 text-sm text-white/55 font-light">
                    {STEPS.map((step, i) => (
                      <li key={step} className="flex gap-3">
                        <span className="w-7 h-7 rounded-full border border-gold/40 bg-gold/10 text-gold flex items-center justify-center text-xs font-bold shrink-0">
                          {i + 1}
                        </span>
                        {step}
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </div>

            <div className="space-y-6 order-2 lg:order-1">
              <p className="luxe-eyebrow">Interactive catalogues</p>
              <h1 className="luxe-display text-4xl sm:text-5xl text-white">
                Create your smart fabric catalogue{' '}
                <span className="luxe-gold-text">today</span>
              </h1>

              <p className="text-base sm:text-lg text-white/55 leading-relaxed max-w-xl font-light">
                From swatch to storyboard: visualise concepts on your fabrics in seconds, surface the right cloth with AI-powered
                search, and put a polished catalogue in your customer’s inbox before the mood passes.
              </p>

              <div className="grid sm:grid-cols-2 gap-4">
                {FEATURES.map(({ icon: Icon, title, desc }) => (
                  <div key={title} className="luxe-glass p-4 hover:luxe-hairline-gold transition-colors duration-500">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl border border-gold/30 bg-gold/10 flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5 text-gold" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-white">{title}</h3>
                        <p className="text-sm text-white/50 mt-1 font-light">{desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
                <button type="button" onClick={onCreateCatalogue} className="luxe-btn-gold group">
                  {isAuthenticated ? 'Create free catalogue' : 'Create free catalogue (Login required)'}
                  <ArrowRight className="w-5 h-5 relative z-10 group-hover:translate-x-0.5 transition-transform" />
                </button>
                <button type="button" onClick={onBack} className="luxe-btn-ghost">
                  Explore Home
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CatalogueInfoPage;
